import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { PushManager } from "../lib/push_manager"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = [
    "planStatus", "planCards", "aiReportSection", "aiReportToggle",
    "aiReportDaySection", "aiReportDayPills",
    "paymentHistory", "paymentList", "monthlyPrice", "yearlyPrice", "lifetimePrice",
    "timezone", "paymentMethodToggle", "pushToggle",
    "installSection", "installTitle", "installHint"
  ]

  connect() {
    this.paymentMethod = "robokassa"
    this.subscription = null
    this.loadSubscription()
    this.initPushToggle()
    this.initInstallButton()

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("payment") === "success") {
      alert(t("settings.payment_success") || "Payment successful!")
      setTimeout(() => this.loadSubscription(), 1000)
    }
  }

  async initPushToggle() {
    if (!this.hasPushToggleTarget) return
    const supported = await PushManager.isSupported()
    if (!supported) {
      this.pushToggleTarget.disabled = true
      return
    }
    const isSubscribed = await PushManager.isSubscribed()
    this.pushToggleTarget.checked = isSubscribed
    this.syncToggleVisual(this.pushToggleTarget, isSubscribed)
  }

  async togglePush() {
    if (!this.hasPushToggleTarget) return
    const checked = this.pushToggleTarget.checked
    if (checked) {
      const success = await PushManager.subscribe()
      if (!success) {
        this.pushToggleTarget.checked = false
        this.syncToggleVisual(this.pushToggleTarget, false)
        return
      }
    } else {
      await PushManager.unsubscribe()
    }
    this.syncToggleVisual(this.pushToggleTarget, checked)
  }

  async loadSubscription() {
    try {
      this.subscription = await apiGet("/api/subscription")
      this.renderSubscription()
    } catch (e) {
      console.error("Failed to load subscription:", e)
    }
  }

  cardMethod(sub) {
    if (sub?.alt_provider_local) return "robokassa"
    if (sub?.alt_provider_intl) return "stripe"
    return null
  }

  renderSubscription() {
    const sub = this.subscription
    if (!sub) return

    const cardMethod = this.cardMethod(sub)
    if (cardMethod) {
      this.paymentMethod = cardMethod
      if (this.hasPaymentMethodToggleTarget) {
        this.paymentMethodToggleTarget.classList.remove("hidden")
        const cardBtn = this.paymentMethodToggleTarget.querySelector("[data-method='robokassa'], [data-method='stripe']")
        if (cardBtn) cardBtn.dataset.method = cardMethod
      }
    } else {
      this.paymentMethod = "telegram_stars"
    }

    this.updatePrices()

    // Update plan status
    if (this.hasPlanStatusTarget) {
      if (sub.premium) {
        let status = "Premium"
        if (sub.plan_type === "lifetime") {
          status += ` (${t("settings.lifetime_badge")})`
        } else if (sub.expires_at) {
          const date = new Date(sub.expires_at).toLocaleDateString(
            window.APP_LOCALE === "ru" ? "ru-RU" : "en-US",
            { day: "numeric", month: "short", year: "numeric" }
          )
          status += ` → ${date}`
        }
        this.planStatusTarget.textContent = status
        this.planStatusTarget.style.color = "#FFD700"
      }
    }

    // Hide plan cards for lifetime users
    if (this.hasPlanCardsTarget && sub.plan_type === "lifetime") {
      this.planCardsTarget.classList.add("hidden")
    }

    // Show AI report section for premium users
    if (this.hasAiReportSectionTarget && sub.premium) {
      this.aiReportSectionTarget.classList.remove("hidden")
      if (this.hasAiReportToggleTarget) {
        this.aiReportToggleTarget.checked = sub.ai_report_enabled
        this.syncToggleVisual(this.aiReportToggleTarget, sub.ai_report_enabled)
      }
      this.renderAiReportDay(sub.ai_report_enabled, sub.ai_report_day)
    }

    // Set timezone dropdown
    if (this.hasTimezoneTarget && sub.timezone) {
      this.timezoneTarget.value = sub.timezone
    }

    // Render payment history
    if (this.hasPaymentListTarget && sub.payments && sub.payments.length > 0) {
      if (this.hasPaymentHistoryTarget) {
        this.paymentHistoryTarget.classList.remove("hidden")
      }
      const html = sub.payments.map(p => {
        const date = new Date(p.created_at).toLocaleDateString(
          window.APP_LOCALE === "ru" ? "ru-RU" : "en-US",
          { day: "numeric", month: "short" }
        )
        const amount = p.payment_method === "robokassa" && p.amount_rub
          ? `${p.amount_rub} ₽`
          : p.payment_method === "stripe"
            ? `${icon("credit-card", "w-3.5 h-3.5 inline")} ${p.plan_type}`
            : `${p.stars_amount} ${icon("star", "w-4 h-4 inline")}`
        return `
          <div class="flex justify-between items-center py-2 text-xs">
            <div class="flex items-center gap-2">
              <span style="color: rgb(16,185,129);">${icon("check", "w-3 h-3 inline")}</span>
              <span class="font-medium">${p.plan_type}</span>
            </div>
            <div class="flex items-center gap-2">
              <span>${amount}</span>
              <span style="color: rgb(148,163,184);">${date}</span>
            </div>
          </div>`
      }).join("")
      this.paymentListTarget.innerHTML = html
    }
  }

  updatePrices() {
    const sub = this.subscription
    if (!sub) return

    const useRub = this.paymentMethod === "robokassa" && sub.rub_prices
    const starPrices = sub.prices || {}
    const rubPrices = sub.rub_prices || {}

    const formatPrice = (planType, suffix) => {
      if (useRub && rubPrices[planType]) {
        return `${rubPrices[planType]} ₽ ${suffix}`
      }
      if (starPrices[planType]) {
        return `${starPrices[planType]} ⭐ ${suffix}`
      }
      return ""
    }

    if (this.hasMonthlyPriceTarget) {
      this.monthlyPriceTarget.textContent = formatPrice("monthly", t("settings.per_month"))
    }
    if (this.hasYearlyPriceTarget) {
      this.yearlyPriceTarget.textContent = formatPrice("yearly", t("settings.per_year"))
    }
    if (this.hasLifetimePriceTarget) {
      this.lifetimePriceTarget.textContent = formatPrice("lifetime", `(${t("settings.one_time")})`)
    }
  }

  togglePaymentMethod(event) {
    const method = event.currentTarget.dataset.method
    this.paymentMethod = method

    this.paymentMethodToggleTarget.querySelectorAll("[data-method]").forEach(btn => {
      const isActive = btn.dataset.method === method
      btn.style.backgroundColor = isActive ? "rgb(99,102,241)" : "transparent"
      btn.style.color = isActive ? "#ffffff" : "rgb(148,163,184)"
    })

    this.updatePrices()
  }

  async selectPlan(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const btn = event.currentTarget
    const plan = btn.dataset.plan
    if (!plan) return

    btn.style.opacity = "0.6"

    try {
      const body = { plan_type: plan }
      if (this.paymentMethod !== "telegram_stars") {
        body.payment_method = this.paymentMethod
      }

      const data = await apiPost("/api/subscription/invoice", body)
      btn.style.opacity = "1"

      if (data.payment_url) {
        window.location.href = data.payment_url
        return
      }

      if (data.checkout_url) {
        window.location.href = data.checkout_url
        return
      }

      if (data.invoice_url) {
        window.open(`https://t.me/coinka_app_bot?startapp=pay_${plan}`, "_blank")
        return
      }

      console.error("Invoice error:", data)
    } catch (e) {
      btn.style.opacity = "1"
      alert(e.message || "Failed to create invoice")
    }
  }

  renderAiReportDay(enabled, selectedDay) {
    if (this.hasAiReportDaySectionTarget) {
      if (enabled) {
        this.aiReportDaySectionTarget.classList.remove("hidden")
      } else {
        this.aiReportDaySectionTarget.classList.add("hidden")
      }
    }

    if (!this.hasAiReportDayPillsTarget) return

    const locale = window.APP_LOCALE || "en"
    const dayNames = locale === "ru"
      ? ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]
      : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

    this.aiReportDayPillsTarget.innerHTML = dayNames.map((name, i) => {
      const selected = i === selectedDay
      const bg = selected ? "rgb(99,102,241)" : "rgb(248,250,252)"
      const color = selected ? "#ffffff" : "inherit"
      return `<button type="button" data-action="click->pwa-settings#selectAiReportDay" data-day="${i}"
                      class="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                      style="background-color: ${bg}; color: ${color};">${name}</button>`
    }).join("")
  }

  async selectAiReportDay(event) {
    const day = parseInt(event.currentTarget.dataset.day)
    try {
      await apiPut("/api/subscription/settings", { ai_report_day: day })
      this.subscription.ai_report_day = day
      this.renderAiReportDay(true, day)
    } catch (e) {
      console.error("Failed to update AI report day:", e)
    }
  }

  async toggleAiReport() {
    const enabled = this.aiReportToggleTarget.checked
    try {
      await apiPut("/api/subscription/settings", { ai_report_enabled: enabled })
      this.subscription.ai_report_enabled = enabled
      this.syncToggleVisual(this.aiReportToggleTarget, enabled)
      this.renderAiReportDay(enabled, this.subscription.ai_report_day)
    } catch (e) {
      console.error("Failed to update AI report setting:", e)
      this.aiReportToggleTarget.checked = !enabled
      this.syncToggleVisual(this.aiReportToggleTarget, !enabled)
    }
  }

  async saveTimezone() {
    const timezone = this.timezoneTarget.value
    try {
      await apiPut("/api/subscription/settings", { timezone })
      alert(t("settings.timezone_saved") || "Timezone saved")
    } catch (e) {
      console.error("Failed to update timezone:", e)
    }
  }

  async changeLanguage(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const lang = event.currentTarget.dataset.lang
    try {
      await apiPut("/api/subscription/settings", { language_code: lang })
      window.location.reload()
    } catch (e) {
      console.error("Failed to change language:", e)
    }
  }

  async exportCSV() {
    if (!networkStatus.online) { alertOffline(); return }
    try {
      const response = await fetch("/api/export/csv", { credentials: "same-origin" })
      if (response.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "export" } }))
        return
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `coinka_export.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error("Export CSV error:", e)
    }
  }

  async exportPDF() {
    if (!networkStatus.online) { alertOffline(); return }
    try {
      const response = await fetch("/api/export/pdf", { credentials: "same-origin" })
      if (response.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "export" } }))
        return
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `coinka_export.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error("Export PDF error:", e)
    }
  }

  linkTelegram() {
    window.open("https://t.me/coinka_app_bot?start=link_pwa", "_blank")
  }

  initInstallButton() {
    if (!this.hasInstallSectionTarget) return

    // Already running as installed PWA — hide install button
    if (window.matchMedia("(display-mode: standalone)").matches || navigator.standalone) return

    // Show install section — works for all browsers
    this.installSectionTarget.classList.remove("hidden")

    // If beforeinstallprompt already fired, we have the native prompt
    if (window.deferredInstallPrompt) {
      this._hasNativePrompt = true
    }

    // Listen for future beforeinstallprompt
    window.addEventListener("pwa-install-available", () => {
      this._hasNativePrompt = true
    })

    // Hide after install
    window.addEventListener("pwa-installed", () => {
      this.installSectionTarget.classList.add("hidden")
    })
  }

  installApp() {
    // If browser supports native install prompt (Chrome, Edge, Samsung Internet)
    if (this._hasNativePrompt && window.deferredInstallPrompt) {
      window.deferredInstallPrompt.prompt()
      window.deferredInstallPrompt.userChoice.then((result) => {
        if (result.outcome === "accepted") {
          this.installSectionTarget.classList.add("hidden")
        }
        window.deferredInstallPrompt = null
        this._hasNativePrompt = false
      })
      return
    }

    // Fallback: show manual instructions for browsers without beforeinstallprompt (Yandex, Firefox, Safari)
    const isRu = (window.APP_LOCALE || "en") === "ru"
    const ua = navigator.userAgent.toLowerCase()

    let instructions
    if (ua.includes("yabrowser") || ua.includes("yowser")) {
      instructions = isRu
        ? "Нажмите ⋮ (меню) → «Добавить на главный экран»"
        : "Tap ⋮ (menu) → 'Add to Home Screen'"
    } else if (/firefox|fxios/i.test(ua)) {
      instructions = isRu
        ? "Нажмите ⋮ (меню) → «Установить»"
        : "Tap ⋮ (menu) → 'Install'"
    } else if (/safari/i.test(ua) && !/chrome/i.test(ua)) {
      instructions = isRu
        ? "Нажмите кнопку «Поделиться» → «На экран Домой»"
        : "Tap Share → 'Add to Home Screen'"
    } else {
      instructions = isRu
        ? "Откройте меню браузера → «Добавить на главный экран» или «Установить»"
        : "Open browser menu → 'Add to Home Screen' or 'Install'"
    }

    alert(instructions)
  }

  syncToggleVisual(toggleEl, isOn) {
    // Tailwind peer-checked handles visuals automatically via CSS
    // Just ensure the checked state is correct
    toggleEl.checked = isOn
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
